import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { Alert, Button, ButtonProps, Form, Modal, Select, message } from 'antd';
import StatusButton from 'components/StatusButton';
import useModal from 'hooks/useModal';
import { formatNumber } from 'utils/numberFormat';
import { useMemo, useState } from 'react';
import { getOfferOptions } from 'services/api/refund';
import { tradeOfferV1 } from 'services/api/return';

function MercadoOffer({
  returnExternalId,
  refundAmount,
  unit,
  onSuccess,
  ...otherProps
}: {
  returnExternalId: string;
  refundAmount: number;
  unit: string;
  onSuccess?: () => void;
} & ButtonProps) {
  const { t } = useTranslate();

  const { visible, show, hide, meta } = useModal<{
    returnExternalId: string;
    options: { value: string; label: string; amount: string }[];
  }>();

  const [percentage, setPercentage] = useState<string>();

  const amount = useMemo(() => {
    const option = meta?.options.find(({ value }) => value === percentage);
    return `${unit}${option ? option.amount : '0.00'}`;
  }, [meta, percentage, unit]);

  const action = (
    <StatusButton
      size="small"
      {...otherProps}
      onClick={() => {
        if (returnExternalId === meta?.returnExternalId) {
          return Promise.resolve().then(() => show());
        } else {
          return getOfferOptions({ platform: 'MERCADO', returnExternalId })
            .then(({ availableOffers }) => {
              show({
                returnExternalId,
                options: availableOffers.map(({ percentage, amount }) => {
                  return {
                    value: percentage,
                    label: formatNumber(Number(percentage) / 100, '0.00%'),
                    amount,
                  };
                }),
              });
              setPercentage(undefined);
            })
            .catch(() => {
              message.error(t('phrase.获取数据失败，请稍后再试'));
            });
        }
      }}
    >
      {t('trade.提出部分退款')}
    </StatusButton>
  );

  const modal = meta !== undefined && (
    <Modal
      visible={visible}
      title={t('trade.提出部分退款')}
      width={600}
      onCancel={() => hide()}
      footer={
        <div>
          <Button onClick={() => hide()}>{t('取消')}</Button>
          <StatusButton
            type="primary"
            disabled={percentage === undefined}
            onClick={() => {
              return percentage !== undefined
                ? tradeOfferV1({
                    platform: 'MERCADO',
                    returnExternalId,
                    operate: 'OFFER',
                    percentage,
                  })
                    .then(() => {
                      message.success('已提出');
                      hide();
                      onSuccess?.();
                    })
                    .catch(() => {
                      message.error(t('phrase.保存失败，请稍后再试'));
                    })
                : Promise.resolve();
            }}
          >
            {t('提出退款')}
          </StatusButton>
        </div>
      }
    >
      <Container>
        <Alert
          banner
          showIcon={false}
          type="warning"
          message={t('在买方接受报价后，此索赔将结束，不再影响您的声誉。')}
        />
        <Form.Item colon={false} label={t('产品价格')}>
          {unit}
          {formatNumber(refundAmount, '0,0.00')}
        </Form.Item>
        <section>
          <Form.Item required colon={false} label={t('退款比例')}>
            <Select
              placeholder={t('refundSetting.请选择')}
              value={percentage}
              onChange={setPercentage}
              options={meta.options}
            />
          </Form.Item>
          <span>=</span>
          <Form.Item colon={false} label={t('给买家退款')}>
            <div style={{ fontSize: '16px', color: '#232426' }}>{amount}</div>
          </Form.Item>
        </section>
      </Container>
    </Modal>
  );

  return (
    <>
      {action}
      {modal}
    </>
  );
}

const Container = styled.div`
  > .ant-alert {
    margin: -24px -24px 24px;
  }
  > section {
    display: flex;
    > * {
      margin-right: 24px;
    }
    > span {
      margin-top: 24px;
    }
    > .ant-form-item {
      width: 120px;
      flex-direction: column;
      align-items: stretch;
      > .ant-form-item-label {
        text-align: left;
      }
    }
  }
`;

export default MercadoOffer;
